// ============================================================
// Validación estricta del payload de envío (README §6).
// Se ejecuta en el backend (Lambda submit-survey) y puede reutilizarse
// en el frontend antes de enviar. Deriva del módulo de preguntas, de
// modo que frontend y backend comparten una sola definición de validez.
// ============================================================
import { z } from 'zod';
import { CHAPTERS, INSTRUMENT_VERSION, QUESTIONS } from './data.js';
import { getOptions, isVisible } from './logic.js';
/** Máximo de caracteres en preguntas abiertas. */
export const TEXT_MAX = 2000;
/** Periodo de medición: AAAA-1 o AAAA-2. */
export const PERIOD_RE = /^\d{4}-[12]$/;
const QUESTION_BY_ID = new Map(QUESTIONS.map((q) => [q.id, q]));
/** Valida un único valor contra su pregunta. Devuelve mensaje de error o null. */
export function validateValue(q, value, answers) {
    switch (q.type) {
        case 'single': {
            const opts = getOptions(q, answers);
            if (typeof value !== 'string' || !opts.includes(value)) {
                return `Opción inválida para ${q.id}`;
            }
            return null;
        }
        case 'likert': {
            if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > 10) {
                return `Valor fuera de rango (entero 0–10) para ${q.id}`;
            }
            return null;
        }
        case 'open': {
            if (typeof value !== 'string')
                return `Texto inválido para ${q.id}`;
            if (value.length > TEXT_MAX)
                return `Texto demasiado largo (máx. ${TEXT_MAX}) para ${q.id}`;
            return null;
        }
        case 'matrix': {
            if (typeof value !== 'object' || value === null || Array.isArray(value)) {
                return `Matriz inválida para ${q.id}`;
            }
            const entries = Object.entries(value);
            for (const [row, col] of entries) {
                if (!q.rows.includes(row))
                    return `Fila desconocida en ${q.id}: ${row}`;
                if (typeof col !== 'string' || !q.columns.includes(col)) {
                    return `Columna inválida en ${q.id} / ${row}`;
                }
            }
            // Obligatoria: todas las filas deben estar calificadas.
            for (const row of q.rows) {
                if (!(row in value)) {
                    return `Falta calificar "${row}" en ${q.id}`;
                }
            }
            return null;
        }
    }
}
/**
 * Reglas transversales sobre el mapa de respuestas:
 *  - cada respuesta corresponde a una pregunta existente y VISIBLE
 *    (coherencia de ramificación);
 *  - cada valor es válido para su tipo;
 *  - toda pregunta visible obligatoria (no abierta) está respondida.
 */
function refineAnswers(answers, ctx) {
    const visible = new Map();
    for (const q of QUESTIONS)
        if (isVisible(q, answers))
            visible.set(q.id, q);
    for (const [id, value] of Object.entries(answers)) {
        if (value === undefined)
            continue;
        const q = QUESTION_BY_ID.get(id);
        if (!q) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['answers', id],
                message: `Pregunta desconocida: ${id}`,
            });
            continue;
        }
        if (!visible.has(id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['answers', id],
                message: `Respuesta a pregunta no visible (incoherencia de ramificación): ${id}`,
            });
            continue;
        }
        const err = validateValue(q, value, answers);
        if (err) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['answers', id], message: err });
        }
    }
    for (const q of visible.values()) {
        if (q.type === 'open')
            continue; // abiertas: siempre opcionales
        const value = answers[q.id];
        if (value === undefined || value === null || value === '') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['answers', q.id],
                message: `Falta respuesta obligatoria: ${q.id}`,
            });
        }
    }
}
/** Esquema del payload de `POST /api/responses`. */
export const submissionSchema = z
    .object({
    version: z.literal(INSTRUMENT_VERSION),
    period: z.string().regex(PERIOD_RE),
    /** Campo trampa anti-bots: debe llegar vacío o ausente. */
    honeypot: z.string().max(0).optional(),
    answers: z.record(z.string(), z.unknown()),
})
    .superRefine((data, ctx) => refineAnswers(data.answers, ctx));
/** Helper de conveniencia: valida sin lanzar. */
export function parseSubmission(input) {
    return submissionSchema.safeParse(input);
}
/**
 * Avance por capítulo a partir de las respuestas actuales. Solo cuenta las
 * preguntas visibles según la ramificación; las abiertas (opcionales) no suman
 * al total. Alimenta el panel de navegación por capítulos del frontend.
 */
export function chapterStatuses(answers) {
    return CHAPTERS.map((chapter) => {
        const required = QUESTIONS.filter((q) => q.chapter === chapter.id && q.type !== 'open' && isVisible(q, answers));
        let answered = 0;
        for (const q of required) {
            const v = answers[q.id];
            if (v !== undefined && v !== '' && validateValue(q, v, answers) === null)
                answered += 1;
        }
        return {
            chapter,
            total: required.length,
            answered,
            complete: required.length > 0 && answered === required.length,
        };
    });
}
//# sourceMappingURL=schema.js.map